<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Taxonomies', path: {name: 'admin-taxonomies'} }]" name="Production Types" no-title/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Production Types">
          <template v-slot:header-right>
            <b-form-select
                class="w-auto mr-2"
                v-model="category_type_filter"
                :options="categoryTypeOptions"
            />
            <no-permission-wrapper :show="!canCreateTaxonomies">
              <b-btn variant="success" @click="openNewModal" :disabled="!canCreateTaxonomies">Add new Production Type</b-btn>
            </no-permission-wrapper>
          </template>
          <b-table
              :fields="table_fields"
              :items="productionTypes"
              table-class="text-nowrap"
              responsive
          >
            <template #cell(name)="data">
              <h4 class="mb-0">{{ data.item.name }}</h4>
            </template>
            <template #cell(categoryTypes)="data">
              <b-badge
                v-if="data.item.categoryTypes.length"
                v-for="(categoryType, key) in data.item.categoryTypes"
                :key="key"
                :variant="categoryType.colorVariant"
                class="mr-1"
              >
                {{ categoryType.name }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn variant="primary" @click.prevent="openEditModal(data.item)" :disabled="!canUpdateTaxonomies">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper :show="!canDeleteTaxonomies">
                <b-btn variant="danger" @click.prevent="confirmDelete(data.item)" :disabled="!canDeleteTaxonomies">Delete</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-production-type-edit-modal
        v-if="edit_production_type"
        :production-type="edit_production_type"
        @cancel="edit_production_type = null"
        @success="onProductionTypeEdited"
        visible
    />
    <admin-production-type-add-modal
        ref="newModal"
        @success="onProductionTypeAdded"
    />
  </b-container>
</template>

<script>
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminProductionTypeEditModal from "../components/AdminProductionTypeEditModal";
import AdminProductionTypeAddModal from "../components/AdminProductionTypeAddModal";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminProductionTypes",
  components: {
    NoPermissionWrapper,
    AdminProductionTypeAddModal, AdminProductionTypeEditModal, AdminBreadcrumb, AdminPreloader, AdminPanel},
  data() {
    return {
      loading: false,
      rows: [],
      table_fields: [
        {
          key: "name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "categoryTypes",
          label: "Category Types",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "actions",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ],
      edit_production_type: null,
      category_type_filter: ""
    }
  },
  computed: {
    canCreateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_create');
    },
    canUpdateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_update');
    },
    canDeleteTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_delete');
    },
    search() {
      return this.$store.state.current.search;
    },
    productionTypes() {
      let productionTypes = [...this.$store.state.data.production_types];
      if (this.search) {
        let search = this.search.toLowerCase();
        productionTypes = productionTypes.filter(pt => pt.name.toLowerCase().indexOf(search) >= 0);
      }
      if (this.category_type_filter) {
        productionTypes = productionTypes.filter(pt => pt.category_types.indexOf(this.category_type_filter) >= 0);
      }
      productionTypes.map((pt, index) => {
        if (pt.category_types.length) {
          let categoryTypes = [];
          for (let i = 0; i < pt.category_types.length; i++) {
            let categoryType = this.getCategoryType(pt.category_types[i]);
            if (categoryType) {
              let colorVariant = 'secondary';
              if (categoryType.is_grow && categoryType.is_manufacture) {
                colorVariant = 'info';
              } else if (categoryType.is_grow) {
                colorVariant = 'success';
              }
              categoryTypes.push({
                id: categoryType.id,
                name: categoryType.name,
                colorVariant
              });
            }
          }
          productionTypes[index].categoryTypes = categoryTypes;
        }
        return index;
      });

      return productionTypes.sort((item1, item2) => {
        let name1 = item1.name.toLowerCase(),
            name2 = item2.name.toLowerCase();
        if (name1 < name2) {
          return -1;
        }
        if (name1 > name2) {
          return 1;
        }
        return 0;
      });
    },
    categoryTypeOptions() {
      let categoryTypes = this.$store.state.data.category_types.map(ct => ({
        value: ct.id,
        text: ct.name
      }));
      categoryTypes.unshift({
        value: '',
        text: 'All Category Types'
      });
      return categoryTypes;
    },
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    openNewModal() {
      if (!this.checkPermission('canCreateTaxonomies')) {
        return;
      }
      this.$refs.newModal.open();
    },
    getCategoryType(categoryTypeId) {
      return this.$store.state.data.category_types.find(ct => ct.id === categoryTypeId);
    },
    openEditModal(productionType) {
      if (!this.checkPermission('canUpdateTaxonomies')) {
        return;
      }
      this.edit_production_type = productionType;
    },
    confirmDelete(productionType) {
      if (!this.checkPermission('canDeleteTaxonomies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm(`You are going to delete ${productionType.name} production type`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_PRODUCTION_TYPES}/${productionType.id}/delete`).then(() => {
            let index = this.productionTypes.findIndex(st => st.id === productionType.id);
            if (index >= 0) {
              let productionTypes = [...this.productionTypes];
              productionTypes.splice(index, 1);
              this.$store.commit('data/setProductionTypes', productionTypes);
            }
            this.$bvToast.toast('Production type has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    onProductionTypeAdded(productionType) {
      this.$bvToast.toast('Production type has been successfully created', {
        title: 'Success!',
        variant: 'success'
      });
      let productionTypes = [...this.productionTypes];
      productionTypes.push(productionType);
      this.$store.commit('data/setProductionTypes', productionTypes);
    },
    onProductionTypeEdited(productionType) {
      this.$bvToast.toast('Production type has been successfully updated', {
        title: 'Success!',
        variant: 'success'
      });
      let productionTypes = [...this.productionTypes];
      let index = productionTypes.findIndex(ct => ct.id === productionType.id);
      if (index >= 0) {
        productionTypes[index] = productionType;
      } else {
        productionTypes.push(productionType);
      }
      this.$store.commit('data/setProductionTypes', productionTypes);
    }
  },
}
</script>
